/**
 * Çözüm Önerileri ekranının çizimi.
 *
 * Eski koddan iki fark: metinler tek yerden (pick) seçiliyor ve butonlar
 * inline onclick yerine delege dinleyiciyle bağlanıyor — böylece modül global
 * bir `scAction` yayınlamak zorunda kalmıyor.
 */

import { legacy, pick } from '../../shell/bridge.ts';
import { calcScores, strengthOf, topFive, type Entry, type ScoreKey } from './scores.ts';

const GRADIENTS: Record<string, string[]> = {
  none: ['#555', '#333'],
  weak: ['hsl(0,84%,70%)', 'hsl(0,84%,50%)', 'hsl(0,84%,35%)'],
  moderate: ['hsl(38,92%,75%)', 'hsl(38,92%,55%)', 'hsl(38,92%,40%)'],
  strong: ['hsl(142,71%,70%)', 'hsl(142,71%,50%)', 'hsl(142,71%,38%)'],
};

const COLORS: Record<string, string> = {
  none: 'var(--t2)', weak: 'var(--red)', moderate: '#ff9800', strong: 'var(--grn)',
};

const BADGES = {
  none: { en: 'No Data' },
  weak: { en: 'Weak' },
  moderate: { en: 'Moderate' },
  strong: { en: 'Strong' },
};

const TITLES: Record<ScoreKey, { en: string }> = {
  prot: { en: '🎖️ Financial Discipline Index' },
  inv: { en: '📈 Financial Growth Index' },
  fit: { en: '💎 Financial Maturity Index' },
};

const DESCS: Record<ScoreKey, { en: string }> = {
  prot: {
    en: 'Measures the strength of your regular and detailed record-keeping habits. Notes, people and category diversity raise the score.',
    },
  inv: {
    en: 'Measures how suitable your income-expense balance is for growth. Savings rate and income diversity are key factors.',
    },
  fit: {
    en: 'Analyzes your financial management maturity and stability. Consistent records, recent data and diverse categories matter.',
    },
};

const BTN_LABELS: Record<ScoreKey, { en: string }> = {
  prot: { en: 'Discipline details' },
  inv: { en: 'Growth analysis' },
  fit: { en: 'Maturity report' },
};

const MEDALS = ['🥇', '🥈', '🥉', '4️⃣', '5️⃣'];
const EXPENSE_COLORS = ['#FF1A1A', '#ff6b35', '#ff9500', '#ffb300', '#ffd000'];
const INCOME_COLORS = ['#43A047', '#00897b', '#00acc1', '#3949ab', '#8e24aa'];

const EXPENSE_TIPS = [
  { en: 'Research alternative suppliers.' },
  { en: 'Compare supplier prices.' },
  { en: 'Optimize processes.' },
  { en: 'Set a budget limit.' },
  { en: 'Review periodically.' },
];

const INCOME_TIPS = [
  { en: 'Strengthen your relationship with this source.' },
  { en: 'Invest more in this income stream.' },
  { en: 'Try a long-term contract.' },
  { en: 'Launch a referral program.' },
  { en: 'Allocate resources to grow this channel.' },
];

function gaugeSvg(score: number | null, gradient: string[]): string {
  const r = 45;
  const half = Math.PI * r;
  const quarter = half / 2;
  const filled = score !== null ? Math.min(score / 100, 1) * -half : -quarter;
  const uid = `g${Math.random().toString(36).slice(2, 6)}`;
  const stops = gradient
    .map((c, i) => `<stop offset="${Math.round((i / (gradient.length - 1)) * 100)}%" stop-color="${c}"/>`)
    .join('');
  return `<svg viewBox="0 0 100 52" style="width:100%;max-width:180px;height:86px" aria-hidden="true">`
    + `<defs><linearGradient id="${uid}" x1="0" y1="0" x2="1" y2="0">${stops}</linearGradient></defs>`
    + `<g fill="none" stroke-width="10" transform="translate(50,51)">`
    + `<circle class="sc-track" r="${r}" stroke="var(--div)"/>`
    + `<circle class="sc-fill" r="${r}" stroke="url(#${uid})" stroke-dasharray="${half} ${half}"`
    + ` stroke-dashoffset="${-quarter}" data-target="${filled}"`
    + ` style="transition:stroke-dashoffset 1.2s cubic-bezier(0.65,0,0.35,1)"/>`
    + `</g></svg>`;
}

function scoreCardsHtml(entries: Entry[]): string {
  const scores = calcScores(entries);
  const cards: Array<{ key: ScoreKey; cls: string }> = [
    { key: 'prot', cls: 'active-exp' },
    { key: 'inv', cls: 'active-inc' },
    { key: 'fit', cls: 'active-fit' },
  ];

  const maxLabel = pick({ en: 'out of 100' });
  const noData = pick({ en: 'Add data to calculate' });

  let h = '<div class="sc-grid">';
  for (const { key, cls } of cards) {
    const score = scores[key];
    const s = strengthOf(score);
    h += '<div class="sc-card">'
      + `<div class="sc-card-top"><div class="sc-card-title">${pick(TITLES[key])}</div>`
      + `<span class="sc-badge ${s}">${pick(BADGES[s])}</span></div>`
      + `<div class="sc-gauge">${gaugeSvg(score, GRADIENTS[s])}`
      + `<div class="sc-score-num"><div class="sc-score-val" style="color:${COLORS[s]}">${score ?? '—'}</div>`
      + `<div class="sc-score-max">${maxLabel}</div></div></div>`
      + `<div class="sc-desc">${pick(DESCS[key])}</div>`
      + `<button class="sc-action${score !== null ? ' ' + cls : ''}" data-score-action="${key}">`
      + `${score !== null ? pick(BTN_LABELS[key]) : noData}</button>`
      + '</div>';
  }
  return h + '</div>';
}

function listHtml(entries: Entry[]): string {
  const esc = legacy.escapeHtml;
  const money = legacy.money;

  const section = (
    type: 'expense' | 'income',
    heading: string,
    colors: string[],
    tips: typeof EXPENSE_TIPS,
    icon: string,
    borderClass: string,
    headingColor: string,
  ) => {
    const rows = topFive(entries, type);
    const byCur = legacy.groupByCurrency(entries.filter((e) => e.type === type)) as Record<string, number>;
    let h = `<div style="font-size:14px;font-weight:700;margin:${type === 'income' ? '20px 0 12px' : '0 0 12px'};color:${headingColor}">${heading}</div>`;
    if (!rows.length) {
      return h + `<div style="text-align:center;padding:32px"><div style="font-size:13px;color:var(--t2)">${legacy.t('no_entries_period')}</div></div>`;
    }
    rows.forEach((item, i) => {
      const total = byCur[item.cur] || item.total;
      const pct = Math.min(100, Math.round((item.total / total) * 100));
      h += `<div class="sol"${borderClass}><div style="display:flex;align-items:flex-start;gap:12px">`
        + `<div style="width:26px;height:26px;border-radius:50%;background:var(--${type === 'expense' ? 'rA' : 'gA'});border:1px solid var(--${type === 'expense' ? 'rB' : 'gB'});display:flex;align-items:center;justify-content:center;font-size:12px;flex-shrink:0">${MEDALS[i]}</div>`
        + `<div style="flex:1"><div style="display:flex;justify-content:space-between;margin-bottom:4px;flex-wrap:wrap;gap:4px">`
        + `<div style="font-size:14px;font-weight:700">${esc(item.label)}</div>`
        + `<div style="font-size:15px;font-weight:700;color:${colors[i]}">${money(item.total)} ${item.cur}</div></div>`
        + `<div style="font-size:12px;color:var(--t2);margin-bottom:6px">${item.cur} <span style="color:${colors[i]};font-weight:700">%${pct}</span></div>`
        + `<div class="pb"><div class="pf" style="width:${pct}%;background:${colors[i]}"></div></div>`
        + `<div style="margin-top:8px;font-size:12px;color:var(--t2);background:var(--c2);border-radius:8px;padding:9px;line-height:1.6">${icon} ${pick(tips[i])}</div>`
        + `</div></div></div>`;
    });
    return h;
  };

  return section('expense', legacy.t('top5_exp'), EXPENSE_COLORS, EXPENSE_TIPS, '💡', '', 'var(--red)')
    + section('income', legacy.t('top5_inc'), INCOME_COLORS, INCOME_TIPS, '🟢', ' style="border-color:var(--gB)"', 'var(--grn)');
}

/** Puan kartındaki butonların eylemi. */
function scoreAction(key: ScoreKey): void {
  if (key === 'prot') {
    legacy.toast(pick({ en: 'Add notes & categorize entries to improve!' }));
  } else if (key === 'inv') {
    legacy.toast(pick({ en: 'Track income to improve investment score!' }));
  } else {
    legacy.navigate('v0', 'ni0');
    legacy.toast(pick({ en: 'Add more entries to boost fitness!' }));
  }
}

let clickHandler: ((e: Event) => void) | null = null;

export function render(): void {
  const cards = document.getElementById('score-cards');
  const list = document.getElementById('slb');
  if (!cards || !list) return;

  if (!legacy.isReady) { cards.innerHTML = ''; list.innerHTML = ''; return; }

  const entries = (legacy.user?.exp ?? []) as Entry[];
  const scoped = (legacy.scopedFinance() ?? []) as Entry[];

  cards.innerHTML = scoreCardsHtml(entries);
  list.innerHTML = listHtml(scoped);

  // Gauge animasyonu: hedefe bir kare sonra geç
  requestAnimationFrame(() => {
    cards.querySelectorAll<SVGCircleElement>('.sc-fill').forEach((circle) => {
      circle.style.strokeDashoffset = circle.dataset.target ?? '0';
    });
  });

  if (!clickHandler) {
    clickHandler = (e: Event) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-score-action]');
      if (btn) scoreAction(btn.dataset.scoreAction as ScoreKey);
    };
    cards.addEventListener('click', clickHandler);
  }
}

/** Modül görünürlükten çıkarken açtığı her şeyi kapatır. */
export function teardown(): void {
  const cards = document.getElementById('score-cards');
  if (cards && clickHandler) cards.removeEventListener('click', clickHandler);
  clickHandler = null;
}
